/**
 * Configuración de Keycloak para diferentes ambientes
 *
 * Este archivo centraliza toda la configuración de Keycloak para facilitar
 * el cambio entre ambientes (desarrollo, staging, producción).
 *
 * Parámetros actuales:
 * - URL: keycloak-dev.sysone.com
 * - Realm: sysone-dev
 * - Client ID: localhost-dev (desarrollo)
 */
// Configuración por ambiente
const keycloakConfigs = {
    development: {
        url: 'https://keycloak-dev.sysone.com',
        realm: 'sysone-dev',
        clientId: 'localhost-dev',
        redirectUri: 'http://localhost:3000',
        postLogoutRedirectUri: 'http://localhost:3000',
        checkLoginIframe: false, // Deshabilitado en desarrollo para evitar problemas de CORS
        enableLogging: true,
    },
    staging: {
        url: 'https://keycloak-dev.sysone.com',
        realm: 'sysone-dev',
        clientId: 'self-service',
        redirectUri: 'https://self-dev.l-one.net',
        postLogoutRedirectUri: 'https://self-dev.l-one.net',
        checkLoginIframe: true,
        enableLogging: true,
    },
    production: {
        url: 'https://keycloak.sysone.com',
        realm: 'sysone-prod',
        clientId: 'self-service',
        redirectUri: 'https://self.l-one.net',
        postLogoutRedirectUri: 'https://self.l-one.net',
        checkLoginIframe: true,
        enableLogging: true,
    },
};
/**
 * Obtiene el ambiente actual basado en variables de entorno
 */
export const getCurrentEnvironment = () => {
    const env = process.env.NODE_ENV;
    const customEnv = process.env.REACT_APP_ENVIRONMENT;
    // Priorizar variable personalizada si existe
    if (customEnv) {
        if (customEnv === 'staging' || customEnv === 'production') {
            return customEnv;
        }
    }
    // Fallback basado en NODE_ENV
    switch (env) {
        case 'production':
            return 'production';
        case 'test':
        case 'development':
        default:
            return 'development';
    }
};
/**
 * Obtiene la configuración de Keycloak para el ambiente actual
 */
export const getKeycloakConfig = () => {
    const environment = getCurrentEnvironment();
    const config = keycloakConfigs[environment];
    if (!config) {
        console.error(`No se encontró configuración para el ambiente: ${environment}`);
        // Fallback a desarrollo
        return keycloakConfigs.development;
    }
    // Log de configuración en desarrollo
    if (config.enableLogging) {
        console.log(`🔐 Keycloak Config [${environment}]:`, {
            url: config.url,
            realm: config.realm,
            clientId: config.clientId,
            redirectUri: config.redirectUri,
        });
    }
    return config;
};
/**
 * Configuración adicional de Keycloak
 */
export const keycloakInitOptions = {
    onLoad: 'check-sso', // Cambiar de vuelta a check-sso para evitar loop infinito
    checkLoginIframe: false, // Se configurará dinámicamente
    silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
    silentCheckSsoFallback: false
};
/**
 * Configuración de timeouts y reintentos
 */
export const keycloakTimeouts = {
    // Tiempo de espera para inicialización (ms)
    initTimeout: 10000,
    // Tiempo de espera para refresh de token (ms)
    refreshTimeout: 5000,
    // Tiempo mínimo antes de expiración para refresh automático (segundos)
    minValiditySeconds: 30,
    // Intervalo para verificar expiración de token (ms)
    checkInterval: 60000,
};
/**
 * URLs de callback para diferentes operaciones
 */
export const getCallbackUrls = (baseUrl) => ({
    login: `${baseUrl}/auth/callback`,
    logout: `${baseUrl}/auth/logout`,
    silentCheckSso: `${baseUrl}/silent-check-sso.html`,
});
/**
 * Roles y permisos mapeados desde Keycloak
 */
export const roleMapping = {
    // Roles de Keycloak -> Permisos de la aplicación
    'admin': ['all'],
    'user': ['consulta-poliza', 'denuncia-siniestros', 'pagos'],
    'agent': ['consulta-poliza', 'denuncia-siniestros', 'cotizadores', 'pagos'],
    'supervisor': ['consulta-poliza', 'denuncia-siniestros', 'cotizadores', 'pagos', 'reports'],
};
/**
 * Configuración de logging para diferentes ambientes
 */
export const loggingConfig = {
    development: {
        level: 'debug',
        logKeycloakEvents: true,
        logTokenRefresh: true,
        logUserActions: true,
    },
    staging: {
        level: 'info',
        logKeycloakEvents: true,
        logTokenRefresh: false,
        logUserActions: false,
    },
    production: {
        level: 'error',
        logKeycloakEvents: false,
        logTokenRefresh: false,
        logUserActions: false,
    },
};
export default {
    getKeycloakConfig,
    getCurrentEnvironment,
    keycloakInitOptions,
    keycloakTimeouts,
    getCallbackUrls,
    roleMapping,
    loggingConfig,
};
